import { faCopy } from '@fortawesome/free-solid-svg-icons'
import { Button, IconButton, SelectDropDown, Typography } from 'procyon-ui'
import React from 'react'
import { enqueueNotification } from 'Utils/Helpers'
import PrettyViewObject from 'Utils/PretteyViewObject'
import { FullScreenContentModal } from 'V2Components'

export default function StepContent({
  type,
  step,
  selectedOS,
  setSelectedOS,
  selectedLinuxArch,
  setSelectedLinuxArch,
  config,
  showInstructionModal,
  setShowInstructionModal
}) {
  const handleCopyAgentUrl = (url) => {
    navigator.clipboard.writeText(url)
    enqueueNotification(`agent url copied to clipboard!`, 'info')
  }

  const handleCopyCmd = (value) => {
    navigator.clipboard.writeText(value)
    enqueueNotification(`command copied to clipboard!`, 'info')
  }

  const handleCopyConfig = React.useCallback(() => {
    const lines = Object.entries(config).map(([key, value]) => `${key}=${value}`)
    const textToCopy = lines.join('\n')

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        enqueueNotification(`Config copied to clipboard!`, 'info')
      })
      .catch((err) => {
        console.error('Could not copy text: ', err)
        enqueueNotification(`Failed to copy config`, 'error')
      })
  }, [config])

  const getInstructionByOsType = (osType) => {
    if (type === 'LinuxAgent') {
      let pkg = {}

      switch (osType) {
        case 'rhel86':
          pkg = {
            title: 'RHEL / CentOS (x86_64)',
            file: 'procyon-agent-x86_64.rpm',
            curl: 'curl -O https://ping-crestone.s3.us-west-2.amazonaws.com/crestone-agent/linux/rpm/x86_64/procyon-agent-x86_64.rpm',
            wget: 'wget https://ping-crestone.s3.us-west-2.amazonaws.com/crestone-agent/linux/rpm/x86_64/procyon-agent-x86_64.rpm',
            install: 'sudo rpm -ivh procyon-agent-x86_64.rpm'
          }
          break

        case 'rhel64':
          pkg = {
            title: 'RHEL / CentOS (aarch64)',
            file: 'procyon-agent-aarch64.rpm',
            curl: 'curl -O https://ping-crestone.s3.us-west-2.amazonaws.com/crestone-agent/linux/rpm/aarch64/procyon-agent-aarch64.rpm',
            wget: 'wget https://ping-crestone.s3.us-west-2.amazonaws.com/crestone-agent/linux/rpm/aarch64/procyon-agent-aarch64.rpm',
            install: 'sudo rpm -ivh procyon-agent-aarch64.rpm'
          }
          break

        case 'debian_amd':
          pkg = {
            title: 'Debian / Ubuntu AMD64',
            file: 'procyon-agent_amd64.deb',
            curl: 'curl -O https://ping-crestone.s3.us-west-2.amazonaws.com/crestone-agent/linux/debian/amd64/procyon-agent_amd64.deb',
            wget: 'wget https://ping-crestone.s3.us-west-2.amazonaws.com/crestone-agent/linux/debian/amd64/procyon-agent_amd64.deb',
            install: 'sudo dpkg -i procyon-agent_amd64.deb'
          }
          break

        case 'debian_arm':
          pkg = {
            title: 'Debian / Ubuntu ARM64',
            file: 'procyon-agent_arm64.deb',
            curl: 'curl -O https://ping-crestone.s3.us-west-2.amazonaws.com/crestone-agent/linux/debian/arm64/procyon-agent_arm64.deb',
            wget: 'wget https://ping-crestone.s3.us-west-2.amazonaws.com/crestone-agent/linux/debian/arm64/procyon-agent_arm64.deb',
            install: 'sudo dpkg -i procyon-agent_arm64.deb'
          }
          break

        default:
          return null
      }

      return (
        <>
          {/* Header */}
          <Typography variant='h5' className='mb-1'>
            Install Linux Agent
          </Typography>
          <Typography variant='body-regular' className='text-gray-600 mb-4'>
            {pkg.title} — follow the steps below on the server.
          </Typography>

          {/* STEP 1 */}
          <Typography variant='body-semiBold'>Step 1 — Download the package</Typography>
          <Typography variant='body-regular' className='text-gray-600 mb-2'>
            Choose <strong>one</strong> of the following methods:
          </Typography>

          <div className='relative'>
            <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm overflow-x-auto'>
              {pkg.curl}
              <IconButton
                style={{ width: 20, position: 'absolute', right: 2 }}
                icon={faCopy}
                variant='gray'
                onClick={() => handleCopyCmd(pkg.curl)}
              />
            </pre>
          </div>

          <Typography variant='body-semiBold' className='text-center my-2 text-gray-500'>
            — OR —
          </Typography>

          <div className='relative'>
            <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm overflow-x-auto'>
              {pkg.wget}
              <IconButton
                style={{ width: 20, position: 'absolute', right: 2 }}
                icon={faCopy}
                variant='gray'
                onClick={() => handleCopyCmd(pkg.wget)}
              />
            </pre>
          </div>

          {/* STEP 2 */}
          <div className='mt-4 relative'>
            <Typography variant='body-semiBold'>Step 2 — Install the package</Typography>
            <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm overflow-x-auto mt-2'>
              {pkg.install}
              <IconButton
                style={{ width: 20, position: 'absolute', right: 2 }}
                icon={faCopy}
                variant='gray'
                onClick={() => handleCopyCmd(pkg.install)}
              />
            </pre>
          </div>

          {/* STEP 3 */}
          <div className='mt-4'>
            <Typography variant='body-semiBold'>Step 3 — Verify installation</Typography>
            <pre className='bg-[#1e1e1e] text-[#dcdcdc] rounded p-2 font-mono text-sm mt-2 relative'>
              systemctl status procyon-agent
              <IconButton
                style={{ width: 20, position: 'absolute', right: 2, top: 1 }}
                icon={faCopy}
                variant='gray'
                onClick={() => handleCopyCmd('systemctl status procyon-agent')}
              />
            </pre>
            <Typography variant='body-regular' className='text-gray-600 mt-1'>
              Status should be <strong>active (running)</strong>
            </Typography>
          </div>

          {/* STEP 4 */}
          <div className='mt-4 rounded-md bg-green-50 border border-green-200 p-3'>
            <Typography variant='body-semiBold'>What happens next?</Typography>
            <ul className='list-disc pl-5 text-sm text-gray-700 mt-1'>
              <li>The agent connects to the controller</li>
              <li>The agent appears in the UI within a few minutes</li>
            </ul>
          </div>
        </>
      )
    }

    if (osType === '' && type === 'WindowAgent') {
      return (
        <>
          <Typography variant='h5' className='mb-4'>
            Instruction for Microsoft Windows
          </Typography>

          <div>
            <Typography variant='body-semiBold'>
              Download the windows guest agent executable(exe)
            </Typography>
            <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm text-wrap wrap-now'>
              {
                'https://ping-crestone.s3.us-west-2.amazonaws.com/windows-guest-agent/WindowsGuestAgent.exe'
              }
              <IconButton
                style={{ width: 20 }}
                icon={faCopy}
                variant='gray'
                onClick={() =>
                  handleCopyCmd(
                    'https://ping-crestone.s3.us-west-2.amazonaws.com/windows-guest-agent/WindowsGuestAgent.exe'
                  )
                }
              />
            </pre>
          </div>
          <div>
            <Typography variant='body-semiBold'>
              Choose an install location(dir) on the box
            </Typography>
            <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm text-wrap wrap-now'>
              {`C:\\Program Files\\PingOne Privilege Guest Agent`}
            </pre>
          </div>
          <div>
            <Typography variant='body-semiBold' className='mt-2'>
              Once installed create a procyon-guest-agent.env file on the box at a temporary
              location and copy config provided in first step. Copy this file into the the install
              location overwriting the file present at the location.
            </Typography>
          </div>
        </>
      )
    }

    const isRhel = osType === 'rhel'
    const isDebianAmd = osType === 'debian_amd'

    const pkg = isRhel
      ? {
          title: 'RHEL / CentOS (RPM x86_64)',
          file: 'procyon-server-agent-x86_64.rpm',
          curl: 'curl -O https://ping-crestone.s3.us-west-2.amazonaws.com/server-guest-agent/rpm/procyon-server-agent-x86_64.rpm',
          wget: 'wget https://ping-crestone.s3.us-west-2.amazonaws.com/server-guest-agent/rpm/procyon-server-agent-x86_64.rpm',
          install: 'sudo rpm -ivh procyon-server-agent-x86_64.rpm'
        }
      : isDebianAmd
        ? {
            title: 'Debian / Ubuntu AMD64',
            file: 'procyon-server-agent_amd64.deb',
            curl: 'curl -O https://ping-crestone.s3.us-west-2.amazonaws.com/server-guest-agent/debian/amd64/procyon-server-agent_amd64.deb',
            wget: 'wget https://ping-crestone.s3.us-west-2.amazonaws.com/server-guest-agent/debian/amd64/procyon-server-agent_amd64.deb',
            install: 'sudo dpkg -i procyon-server-agent_amd64.deb'
          }
        : {
            title: 'Debian / Ubuntu ARM64',
            file: 'procyon-server-agent_arm64.deb',
            curl: 'curl -O https://ping-crestone.s3.us-west-2.amazonaws.com/server-guest-agent/debian/arm64/procyon-server-agent_arm64.deb',
            wget: 'wget https://ping-crestone.s3.us-west-2.amazonaws.com/server-guest-agent/debian/arm64/procyon-server-agent_arm64.deb',
            install: 'sudo dpkg -i procyon-server-agent_arm64.deb'
          }

    return (
      <>
        {/* Header */}
        <Typography variant='h5' className='mb-1'>
          Install Server Guest Agent
        </Typography>
        <Typography variant='body-regular' className='text-gray-600 mb-4'>
          {pkg.title} — follow the steps below on the server.
        </Typography>

        {/* STEP 1 */}
        <Typography variant='body-semiBold'>Step 1 — Download the package</Typography>
        <Typography variant='body-regular' className='text-gray-600 mb-2'>
          Choose <strong>one</strong> of the following methods:
        </Typography>

        <div className='relative'>
          <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm overflow-x-auto'>
            {pkg.curl}
            <IconButton
              style={{ width: 20, position: 'absolute', right: 2 }}
              icon={faCopy}
              variant='gray'
              onClick={() => handleCopyCmd(pkg.curl)}
            />
          </pre>
        </div>

        <Typography variant='body-semiBold' className='text-center my-2 text-gray-500'>
          — OR —
        </Typography>

        <div className='relative'>
          <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm overflow-x-auto'>
            {pkg.wget}
            <IconButton
              style={{ width: 20, position: 'absolute', right: 2 }}
              icon={faCopy}
              variant='gray'
              onClick={() => handleCopyCmd(pkg.wget)}
            />
          </pre>
        </div>

        {/* STEP 2 */}
        <div className='mt-4 relative'>
          <Typography variant='body-semiBold'>Step 2 — Install the package</Typography>
          <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm overflow-x-auto mt-2'>
            {pkg.install}
            <IconButton
              style={{ width: 20, position: 'absolute', right: 2 }}
              icon={faCopy}
              variant='gray'
              onClick={() => handleCopyCmd(pkg.install)}
            />
          </pre>
        </div>

        {/* STEP 3 */}
        <div className='mt-4'>
          <Typography variant='body-semiBold'>Step 3 — Verify installation</Typography>
          <pre className='bg-[#1e1e1e] text-[#dcdcdc] rounded p-2 font-mono text-sm mt-2 relative'>
            systemctl status procyon-guest-agent
            <IconButton
              style={{ width: 20, position: 'absolute', right: 2, top: 1 }}
              icon={faCopy}
              variant='gray'
              onClick={() => handleCopyCmd('systemctl status procyon-guest-agent')}
            />
          </pre>
          <Typography variant='body-regular' className='text-gray-600 mt-1'>
            Status should be <strong>active (running)</strong>
          </Typography>
        </div>

        {/* STEP 4 */}
        <div className='mt-4 rounded-md bg-green-50 border border-green-200 p-3'>
          <Typography variant='body-semiBold'>What happens next?</Typography>
          <ul className='list-disc pl-5 text-sm text-gray-700 mt-1'>
            <li>The agent connects to the controller</li>
            <li>The proxy container starts automatically</li>
            <li>The server appears in the UI within a few minutes</li>
          </ul>
        </div>
      </>
    )
  }

  switch (step) {
    case 0:
      return (
        <div>
          <Typography variant='h3'>Create Env File</Typography>
          {type === 'WindowAgent' ? (
            <div className='space-y-2 my-4'>
              <Typography variant='body-semiBold'>
                Create the directory (if not already present):
              </Typography>
              <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm overflow-x-auto'>
                {'mkdir "C:\\Program Files\\PingOne Privilege Guest Agent"'}
                <IconButton
                  style={{ width: 20 }}
                  icon={faCopy}
                  variant='gray'
                  onClick={() =>
                    handleCopyCmd('mkdir "C:\\Program Files\\PingOne Privilege Guest Agent"')
                  }
                />
              </pre>
              <Typography variant='body-semiBold'>
                {' '}
                Then open Notepad and create the env file:
              </Typography>
              <pre className='flex items-center justify-between bg-[#1e1e1e] text-[#dcdcdc] rounded p-2 font-mono text-sm overflow-x-auto'>
                {'notepad "C:\\Program Files\\pingone-privilege-agent.env"'}
                <IconButton
                  style={{ width: 20 }}
                  icon={faCopy}
                  variant='gray'
                  onClick={() =>
                    handleCopyCmd('notepad "C:\\Program Files\\pingone-privilege-guest-agent.env"')
                  }
                />
              </pre>
              <div className='space-y-1 mt-5'>
                <Typography variant='body-regular'>
                  Paste the below content inside the file & save it.
                </Typography>
                <div className='relative'>
                  <PrettyViewObject data={config} />
                  <IconButton
                    style={{ width: 20, position: 'absolute', bottom: 10, right: 10 }}
                    icon={faCopy}
                    variant='gray'
                    onClick={() => handleCopyConfig()}
                  />
                </div>
              </div>
            </div>
          ) : type === 'LinuxAgent' ? (
            <div className='space-y-2 my-4'>
              <Typography variant='body-semiBold'>Create the file and open it:</Typography>
              <pre className='flex items-center justify-between bg-[#1e1e1e] text-[#dcdcdc] rounded p-2 font-mono text-sm overflow-x-auto'>
                {'sudo vi /var/lib/procyon/procyon-agent.env'}
                <IconButton
                  style={{ width: 20 }}
                  icon={faCopy}
                  variant='gray'
                  onClick={() => handleCopyCmd('sudo vi /var/lib/procyon/procyon-agent.env')}
                />
              </pre>
              <div className='space-y-1 mt-5'>
                <Typography variant='body-semiBold'>
                  Paste the below content inside the file & save it.
                </Typography>
                <div className='relative pb-2'>
                  <PrettyViewObject
                    data={{
                      CERT_KEY_PATH: '/var/lib/procyon/certs',
                      ONBOARD_LINK: 'https://privilege.ort-one-pingone.com(controller url)'
                    }}
                  />
                  <IconButton
                    style={{ width: 20, position: 'absolute', bottom: 10, right: 10 }}
                    icon={faCopy}
                    variant='gray'
                    onClick={() => {
                      const textToCopy = `CERT_KEY_PATH=/var/lib/procyon/certs\nONBOARD_LINK=https://privilege.ort-one-pingone.com(controller url)`
                      navigator.clipboard.writeText(textToCopy)
                      enqueueNotification(`Config copied to clipboard!`, 'info')
                    }}
                  />
                </div>

                <Typography variant='body-regular'>
                  <span className='font-semibold'>Note:</span> Get the onboarding link from the
                  admin and Update the onboard_link parameter with the URL
                </Typography>
              </div>
            </div>
          ) : (
            <div className='space-y-2 my-4'>
              <Typography variant='body-semiBold'>
                Create the directory (if not already present):
              </Typography>

              <pre className='bg-[#1e1e1e] text-[#dcdcdc] flex items-center justify-between rounded p-2 font-mono text-sm overflow-x-auto'>
                {'sudo mkdir -p /var/lib/procyon'}
                <IconButton
                  style={{ width: 20 }}
                  icon={faCopy}
                  variant='gray'
                  onClick={() => handleCopyCmd('sudo mkdir -p /var/lib/procyon')}
                />
              </pre>
              <Typography variant='body-semiBold'>Create the file and open it:</Typography>
              <pre className='flex items-center justify-between bg-[#1e1e1e] text-[#dcdcdc] rounded p-2 font-mono text-sm overflow-x-auto'>
                {'sudo vi /var/lib/procyon/procyon-guest-agent.env'}
                <IconButton
                  style={{ width: 20 }}
                  icon={faCopy}
                  variant='gray'
                  onClick={() => handleCopyCmd('sudo vi /var/lib/procyon/procyon-guest-agent.env')}
                />
              </pre>
              <div className='space-y-1 mt-5'>
                <Typography variant='body-regular'>
                  Paste the below content inside the file & save it.
                </Typography>
                <div className='relative'>
                  <PrettyViewObject data={config} />
                  <IconButton
                    style={{ width: 20, position: 'absolute', bottom: 10, right: 10 }}
                    icon={faCopy}
                    variant='gray'
                    onClick={() => handleCopyConfig()}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )
    case 1:
      return (
        <div className='space-y-4'>
          <Typography variant='h3'>
            {type === 'WindowAgent'
              ? 'Install Window Guest Agent'
              : type === 'LinuxAgent'
                ? 'Install Linux Agent'
                : 'Install Server Guest Agent'}
          </Typography>
          {type === 'WindowAgent' ? (
            <Typography variant='body-regular'>
              Download the windows guest agent executable(exe) setup file and run  it in your
              windows domain controller windows box.
            </Typography>
          ) : (
            <Typography variant='body-regular'>
              Download and install the server guest agent based on your operating system and
              architecture. The service will start automatically after installation, bringing up the
              proxy container.
            </Typography>
          )}

          <div className='space-y-2'>
            <Typography variant='h5' className='font-semibold'>
              Download for your OS:
            </Typography>

            {type === 'WindowAgent' ? (
              <div className='flex flex-col md:flex-row gap-3 items-start'>
                <Button
                  variant='primary'
                  style={{
                    height: '38px'
                  }}
                  onClick={() =>
                    handleCopyAgentUrl(
                      'https://ping-crestone.s3.us-west-2.amazonaws.com/windows-guest-agent/WindowsGuestAgent.exe'
                    )
                  }
                >
                  Copy Microsoft Windows Agent Url
                  <IconButton style={{ width: 20 }} icon={faCopy} variant='primary' />
                </Button>
                <Button
                  variant='grayBlue'
                  style={{
                    height: '38px'
                  }}
                  onClick={() => setShowInstructionModal(true)}
                >
                  Show Instructions
                </Button>
              </div>
            ) : type === 'LinuxAgent' ? (
              <div className='flex flex-col md:flex-row gap-3 items-start'>
                <SelectDropDown
                  menuItems={[
                    { label: 'Select your OS / Architecture', value: '', disabled: true },
                    {
                      label: 'RHEL / CentOS (x86_64)',
                      value: 'rhel86',
                      selected: selectedLinuxArch === 'rhel86'
                    },
                    {
                      label: 'RHEL / CentOS (aarch64)',
                      value: 'rhel64',
                      selected: selectedLinuxArch === 'rhel64'
                    },
                    {
                      label: 'Debian / Ubuntu AMD64',
                      value: 'debian_amd',
                      selected: selectedLinuxArch === 'debian_amd'
                    },
                    {
                      label: 'Debian / Ubuntu ARM64',
                      value: 'debian_arm',
                      selected: selectedLinuxArch === 'debian_arm'
                    }
                  ]}
                  value={selectedLinuxArch}
                  onChange={(val) => setSelectedLinuxArch(val?.target?.value ?? val)}
                />

                {selectedLinuxArch && (
                  <>
                    <Button
                      variant='grayBlue'
                      style={{
                        height: '38px'
                      }}
                      onClick={() => setShowInstructionModal(selectedLinuxArch)}
                    >
                      Show Instructions
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <div className='flex flex-col md:flex-row gap-3 items-start'>
                <SelectDropDown
                  menuItems={[
                    { label: 'Select your OS / Architecture', value: '', disabled: true },
                    {
                      label: 'RHEL / CentOS (RPM x86_64)',
                      value: 'rhel',
                      selected: selectedOS === 'rhel'
                    },
                    {
                      label: 'Debian / Ubuntu AMD64',
                      value: 'debian_amd',
                      selected: selectedOS === 'debian_amd'
                    },
                    {
                      label: 'Debian / Ubuntu ARM64',
                      value: 'debian_arm',
                      selected: selectedOS === 'debian_arm'
                    }
                  ]}
                  value={selectedOS}
                  onChange={(val) => setSelectedOS(val?.target?.value ?? val)}
                />

                {selectedOS && (
                  <>
                    <Button
                      variant='grayBlue'
                      style={{
                        height: '38px'
                      }}
                      onClick={() => setShowInstructionModal(selectedOS)}
                    >
                      Show Instructions
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          {showInstructionModal && (
            <FullScreenContentModal
              children={
                <>
                  {getInstructionByOsType(
                    type === 'LinuxAgent'
                      ? selectedLinuxArch
                      : type === 'WindowAgent'
                        ? ''
                        : selectedOS
                  )}
                  <div className='mt-4 flex justify-end gap-4'>
                    <Button
                      variant='gray'
                      onClick={() => {
                        setShowInstructionModal(!showInstructionModal)
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </>
              }
            />
          )}
        </div>
      )

    default:
      return null
  }
}
